import type { FC, ReactNode } from "react";

import styled, { css } from "styled-components";

import { colorsPalettes, mixins, setFontVariant } from "@similarweb/styles";
import { DropdownButton } from "@similarweb/ui-components";
import { EllipsisDropdownItem } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import type { IDropdownButtonProps } from "@similarweb/ui-components";
import type { IEllipsisDropdownItemProps } from "@similarweb/ui-components";

type EllipsisDropdownItemProps = IEllipsisDropdownItemProps & { preventDefault?: boolean };

type EllipsisDropdownItemType = FC<EllipsisDropdownItemProps>;

export const StyledAccountSelect = styled.div`
    position: relative;
`;

const StyledLabel = styled.span`
    text-transform: uppercase;
    position: absolute;
    right: 0;
    top: -16px;
    line-height: 1;
`;

export const StyledNewAccountLabel = styled(StyledLabel)`
    ${setFontVariant("tag", colorsPalettes.orange[400])};
`;

export const StyledExistAsLeadLabel = styled(StyledLabel)`
    ${setFontVariant("tag", colorsPalettes.red["s100"])};
`;

export const StyledNewAccountItem = styled<EllipsisDropdownItemType>(EllipsisDropdownItem)`
    ${mixins.setFont({ $color: colorsPalettes.carbon[500], $size: 14 })};
    line-height: 16px;
    padding-bottom: 1px;
    position: relative;

    > div {
        max-width: 100%;
    }

    ${(props) =>
        !props.iconName &&
        css`
            padding-left: 32px;

            &:before {
                content: "";
                position: absolute;
                left: 16px;
                top: calc(50% - 4px);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: ${colorsPalettes.orange[400]};
            }
        `}

    &::after {
        content: "";
        display: block;
        background-color: ${colorsPalettes.carbon[100]};
        width: calc(100% - 20px);
        height: 1px;
        left: 10px;
        bottom: 0;
        position: absolute;
    }
`;

export const StyledAccountItem = styled<FC<EllipsisDropdownItemProps & { extendedPadding: boolean }>>(
    EllipsisDropdownItem,
)`
    padding-left: ${(props) => (props.extendedPadding ? "48px" : "16px")};

    > div {
        max-width: 100%;
    }
`;

export const StyledAccountsGroupItem = styled<EllipsisDropdownItemType>(EllipsisDropdownItem)`
    font-weight: 500;
    cursor: auto;

    &:hover,
    &:active {
        background-color: ${colorsPalettes.carbon[0]};
    }
`;

type WrappedDropdownButtonProps = Omit<IDropdownButtonProps, "cssClass"> & {
    className?: string;
    children?: ReactNode;
};

const WrappedDropdownButton = ({ className, ...rest }: WrappedDropdownButtonProps) => {
    return (
        <DropdownButton
            {...rest}
            cssClass={className}
        />
    );
};

export const StyledDropdownButton = styled(WrappedDropdownButton)`
    &.DropdownButton,
    &.DropdownButton--focus,
    &.DropdownButton--opened,
    &:active {
        background-color: ${colorsPalettes.carbon[0]};
        border-color: ${colorsPalettes.midnight[50]};
        color: ${colorsPalettes.carbon[400]};
    }

    & .DropdownButton--triangle {
        color: ${colorsPalettes.carbon[400]};
    }
`;

export const StyledDropdownDisabledButton = styled(WrappedDropdownButton)`
    background-color: ${colorsPalettes.bluegrey[200]};
    border-color: ${colorsPalettes.midnight[50]};

    & .DropdownButton--triangle {
        color: ${colorsPalettes.midnight[100]};
    }

    &:hover {
        box-shadow: unset;
    }
`;

export const StyledDotsLoader = styled(DotsLoader)`
    position: absolute;
    right: 6px;
    top: 26px;
`;
